// Thin orchestrator for a single track row. Delegates the sidebar controls to
// <TrackSidebar/> and the waveform/timeline pane to <TrackTimeline/>. Owns only
// the shared "nudge mode" toggle state that both halves need to read.
import React, { useState } from "react";
import TrackSidebar from "./TrackSidebar";
import TrackTimeline from "./TrackTimeline";

export default function TrackRow({
  track, loopBars, loopDuration, playing, playPos,
  onCopySection,
  onDelete, onChange, onDrumify, onOneTapGroove, drumifying,
  onSync, syncing, onEdit, onInstrument, onChordify,
  onNudgeOnset, onDeleteOnset, onDuplicateOnset, onDuplicateToBars,
  onDeleteMany, onDuplicateMany, onClassifyOnset, onNudgeMany, onResizeOnset,
  onPitchShiftOnset, onPitchShiftMany, onAutoTune, onPasteAtTime,
  onSnapAllNotes,
  bpm, busy,
  keyRoot, keyMode, onChangeKey,
}) {
  const trackDur = track.duration || 0;
  const onsets = track.onsets || [];
  // `canSync` still hides Sync on already-synced tracks (running Sync again
  // would be a no-op). Every other creative action (Drumify / Instrument /
  // Chordify / Edit Notes) is now available on synced tracks too — synced
  // sources are still perfectly good inputs for those transforms, and
  // hiding them on synced siblings was the source of the "duplicate track
  // loses its buttons" complaint.
  const canDrumify = track.kind === "rhythm" && track.buffer;
  const canSync = track.buffer && !track.is_synced;
  const canEdit = track.buffer && trackDur > 0.5;
  const canNudge = track.buffer && onsets.length > 0;
  // Melody-style synthesis is offered on anything pitched (not drums).
  const canInstrument = track.buffer
    && (track.kind === "melody" || track.kind === "vocal");

  const [nudgeMode, setNudgeMode] = useState(false);

  return (
    <div
      className="bb-editor-card rounded-2xl overflow-hidden grid grid-cols-12"
      data-testid={`track-row-${track.id}`}
    >
      <TrackSidebar
        track={track}
        onDelete={onDelete}
        onChange={onChange}
        canSync={canSync}
        onSync={onSync}
        syncing={syncing}
        canDrumify={canDrumify}
        onDrumify={onDrumify}
        onOneTapGroove={onOneTapGroove}
        drumifying={drumifying}
        canInstrument={canInstrument}
        onInstrument={onInstrument}
        onChordify={onChordify}
        canEdit={canEdit}
        onEdit={onEdit}
        loopBars={loopBars}
        onCopySection={onCopySection}
        canNudge={canNudge}
        nudgeMode={nudgeMode}
        setNudgeMode={setNudgeMode}
        onSnapAllNotes={onSnapAllNotes}
        busy={busy}
      />
      <TrackTimeline
        track={track}
        loopBars={loopBars}
        loopDuration={loopDuration}
        playing={playing}
        playPos={playPos}
        bpm={bpm}
        nudgeMode={nudgeMode}
        onNudgeOnset={onNudgeOnset}
        onDeleteOnset={onDeleteOnset}
        onDuplicateOnset={onDuplicateOnset}
        onDuplicateToBars={onDuplicateToBars}
        onDeleteMany={onDeleteMany}
        onDuplicateMany={onDuplicateMany}
        onClassifyOnset={onClassifyOnset}
        onNudgeMany={onNudgeMany}
        onResizeOnset={onResizeOnset}
        onPitchShiftOnset={onPitchShiftOnset}
        onPitchShiftMany={onPitchShiftMany}
        onAutoTune={onAutoTune}
        onPasteAtTime={onPasteAtTime}
        keyRoot={keyRoot}
        keyMode={keyMode}
        onChangeKey={onChangeKey}
      />
    </div>
  );
}
